import { useState, useEffect } from 'react'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import MoneyInput from 'components/Inputs/MoneyInput'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import bankingWithdrawValidator from 'validators/bankingWithdrawValidator'
import WithdrawalAccountForm from '../WithdrawalAccountForm'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'
import { FaUniversity } from 'react-icons/fa'

import {
  Card,
  Row,
  Form,
  FormField,
  FieldLabel,
  FormActions,
  HighlightRow,
  CardHeader,
  CardTitle,
  Caption,
  DataList,
  DataRow,
  DataLabel,
  DataValue,
  StatusBadge,
  Divider,
  InfoText,
  WarningText,
  PlainMoneyText
} from '../../styles'

export default function WithdrawForm ({
  availableBalance,
  requestConfig,
  onCompleted = () => {}
}) {
  const [loading, setLoading] = useState (true)
  const [target, setTarget] = useState (null)
  const [receipt, setReceipt] = useState (null)
  const [showConfirmDialog, setShowConfirmDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [requestError, setRequestError] = useState (null)

  useEffect (() => {
    getTarget ()
  }, [])

  const toggleConfirmDialog = () => setShowConfirmDialog (prev => !prev)
  const toggleOkDialog = () => setShowOkDialog (prev => !prev)

  const getTarget = async () => {
    try {
      setLoading (true)
      const { data } = await cutileiApi.get (
        '/banking_account/withdrawal_target', requestConfig
      )

      setTarget (data)
      setRequestError (null)
    } catch (error) {
      console.log (error.response ?? error)
      setRequestError (
        error.response?.data?.message ?? 'Não foi possível carregar sua conta de saque'
      )
    } finally {
      setLoading (false)
    }
  }

  if (loading) return <Loading style={{alignSelf: 'center'}}/>

  if (!target) return (
    <Card>
      <CardTitle>Não foi possível carregar sua conta de saque</CardTitle>
      <Caption>{requestError}</Caption>
    </Card>
  )

  const bankAccount = target.bankAccount
  const fee = Number (target.fee ?? 0)
  const minValue = Number (target.minValue ?? 0)

  /*
    Sem conta de saque o saque não tem destino, e o cadastro acontece aqui mesmo, na hora em
    que ele faz falta. A API responde 200 com `bankAccount` nulo justamente para separar este
    passo do fluxo de um erro, e devolve o destino já completo depois do cadastro.
  */
  if (!bankAccount) return (
    <WithdrawalAccountForm
      requestConfig={requestConfig}
      onRegistered={setTarget}
    />
  )

  return (
    <Formik
      validationSchema={bankingWithdrawValidator}
      /*
        O saldo é recarregado após cada saque: sem reinicializar, o teto da validação
        continuaria sendo o saldo de antes da operação.
      */
      enableReinitialize
      initialValues={{
        availableBalance,
        minValue,
        value: 0
      }}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          setSubmitting (true)
          setRequestError (null)

          const { data: transaction } = await cutileiApi.post ('/banking_account/withdrawals', {
            value: values.value
          }, requestConfig)

          setSubmitting (false)
          setReceipt (transaction)
          resetForm ({values: {availableBalance, minValue, value: 0}})
          toggleOkDialog ()
          onCompleted ()
        } catch (error) {
          setSubmitting (false)
          console.log (error.response ?? error)
          setRequestError (
            error.response?.data?.message ?? 'Não foi possível realizar o saque'
          )
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        validateForm,
        handleBlur,
        handleSubmit
      }) => {
        /*
          A confirmação só abre com o formulário válido: o diálogo mostra o valor que vai
          sair da conta, e exibi-lo a partir de um campo inválido pediria a confirmação de
          um saque que a API recusaria em seguida.
        */
        const handleConfirmRequest = async () => {
          const formErrors = await validateForm ()

          if (Object.keys (formErrors).length > 0) return handleSubmit ()

          toggleConfirmDialog ()
        }

        const netValue = Math.max (Number (values.value) - fee, 0)

        return (
          <Form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle>Sacar para a minha conta bancária</CardTitle>
              <FaUniversity size={16} color='#FF9800'/>
            </CardHeader>
            <Caption>
              {`O saque cai na conta bancária abaixo. Valor mínimo de `
              + `R$ ${minValue.toFixed (2).replace ('.', ',')}`}
            </Caption>
            <Divider/>
            <DataList>
              <DataRow>
                <DataLabel>Banco</DataLabel>
                <DataValue>{bankAccount.bankName ?? 'Não informado'}</DataValue>
              </DataRow>
              <DataRow>
                <DataLabel>Agência e conta</DataLabel>
                <DataValue>
                  {BankingService.getAccountNumberLabel (bankAccount)}
                </DataValue>
              </DataRow>
              {bankAccount.ownerName && (
                <DataRow>
                  <DataLabel>Titular</DataLabel>
                  <DataValue>{bankAccount.ownerName}</DataValue>
                </DataRow>
              )}
            </DataList>
            {/*
              O Asaas aceita mais de uma conta bancária e decide sozinho qual é a principal, sem
              oferecer como trocar essa escolha — então a API recusa um segundo cadastro em vez
              de oferecer uma troca que não mudaria o destino do dinheiro.
            */}
            <InfoText>
              Para trocar a conta que recebe os seus saques, fale com o suporte da Cutilei
            </InfoText>
            <Row>
              <FormField>
                <FieldLabel>Valor do saque</FieldLabel>
                <MoneyInput
                  name='value'
                  placeholder='R$ 0,00'
                  value={values.value}
                  maxValue={availableBalance}
                  onValueChange={setFieldValue}
                  onBlur={handleBlur ('value')}
                />
                {touched.value && errors.value && (
                  <WarningText>{errors.value}</WarningText>
                )}
              </FormField>
              <FormField>
                <FieldLabel>Tarifa de saque</FieldLabel>
                <HighlightRow style={{height: 40}}>
                  <DataLabel>Cobrada pelo banco</DataLabel>
                  <PlainMoneyText value={fee}/>
                </HighlightRow>
              </FormField>
            </Row>
            {requestError && <WarningText>{requestError}</WarningText>}
            <HighlightRow>
              <DataLabel>Você receberá</DataLabel>
              <PlainMoneyText value={netValue}/>
            </HighlightRow>
            <FormActions>
              <MainButton
                label='Sacar'
                onClick={handleConfirmRequest}
                loading={isSubmitting}
                width={150}
              />
            </FormActions>
            {receipt && (
              <>
                <Divider/>
                <Row style={{justifyContent: 'space-between', alignItems: 'center'}}>
                  <InfoText>Último saque</InfoText>
                  <StatusBadge color={BankingService.getTransactionStatusColor (receipt.status)}>
                    {BankingService.getTransactionStatusLabel (receipt.status)}
                  </StatusBadge>
                </Row>
              </>
            )}
            <AlertDialog
              visible={showConfirmDialog}
              title='Confirmar saque'
              message={
                `Sacar ${Number (values.value).toLocaleString ('pt-BR', {
                  style: 'currency', currency: 'BRL'
                })} para ${bankAccount.bankName}, conta `
                + `${bankAccount.account}-${bankAccount.accountDigit}?`
              }
              confirmText='Sacar'
              cancelText='Cancelar'
              onConfirm={handleSubmit}
              onClose={toggleConfirmDialog}
            />
            <AlertDialog
              visible={showOkDialog}
              title='Saque solicitado'
              message={
                'O seu saque foi enviado ao banco. O saldo é atualizado assim que a '
                + 'transferência for liquidada'
              }
              confirmText='Ok'
              onClose={toggleOkDialog}
            />
          </Form>
        )
      }}
    </Formik>
  )
}
